/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createBatchTestStore } from 'src/luis/core/stores/batchTestStore';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { BatchTestDataPointSwagger } from 'src/luis/projects/testPane/types/swagger/BatchTestDataPointSwagger';
import { BatchTestSwagger } from 'src/luis/projects/testPane/types/swagger/BatchTestSwagger';
import { EndpointUtteranceResult } from 'src/luis/projects/testPane/types/types';
import { PredictionSwagger } from 'src/luis/projects/utterances/services/mappers';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStoreHouse } from 'src/luis/types/stores/entities/EntityStoreHouse';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { IntentStoreHouse } from 'src/luis/types/stores/intents/IntentStoreHouse';
import { BatchTestResultItem } from 'src/luis/types/stores/testPane/BatchTestResultItem';
import { BatchTestStore } from 'src/luis/types/stores/testPane/BatchTestStore';
import { EntityConfusion } from 'src/luis/types/stores/testPane/EntityConfusion';

/**
 * Creates an batch test store from the swagger received
 * from the api.
 *
 * @param batchTestSwagger The batch test object received from the api.
 * @returns An batch test store.
 */
export const batchTestSwaggerToBatchTestStore = (batchTestSwagger: BatchTestSwagger): BatchTestStore =>
    createBatchTestStore({
        state: 'notRunning',
        id: batchTestSwagger.id,
        lastResult: batchTestSwagger.lastTestUtteranceSuccessCount,
        lastRunDate: batchTestSwagger.lastTestDateTime ? new Date(batchTestSwagger.lastTestDateTime) : null,
        name: batchTestSwagger.name,
        size: batchTestSwagger.utteranceCount
    });

/**
 * Creates an batch test swagger object from the batch test store
 *
 * @param batchTestStore The batch test store to map.
 * @returns An batch test swagger object.
 */
export const batchTestStoreToBatchTestSwagger = (batchTestStore: Partial<BatchTestStore>): BatchTestSwagger => ({
    id: batchTestStore.id.toString(),
    name: batchTestStore.name,
    lastTestDateTime: batchTestStore?.lastRunDate.toDateString(),
    utteranceCount: batchTestStore.size,
    lastTestUtteranceSuccessCount: batchTestStore?.lastResult
});

/**
 * Creates an dataset item from dataset swagger object
 *
 * @param datasetSwagger The swagger object to map.
 * @param entitiesHouse The entities store house.
 * @param intentsHouse The intents store house.
 * @returns An batch test swagger object.
 */
export const batchTestResultItemSwaggerTobatchTestResultItemStore = ({
    datasetSwagger,
    entitiesHouse,
    intentsHouse
}: {
    datasetSwagger: BatchTestDataPointSwagger;
    entitiesHouse: EntityStoreHouse;
    intentsHouse: IntentStoreHouse;
}): BatchTestResultItem => {
    const entityByName = (entityName: string): Entity => {
        const entity = entitiesHouse.findByName(entityName);

        if (!entity) {
            for (const simpleEntityStore of entitiesHouse.simpleEntities) {
                const foundEntity = (<SimpleEntityStore>simpleEntityStore).getChildByName(entityName);
                if (foundEntity) {
                    return { ...foundEntity, roles: foundEntity.entityRoleStoreHouse ? foundEntity.entityRoleStoreHouse.items : [] };
                }
            }
            for (const hierarchicalEntityStore of entitiesHouse.hierarchicalEntities) {
                const foundEntity = (<HierarchicalEntityStore>hierarchicalEntityStore).children.find(
                    c => `${hierarchicalEntityStore.name}::${c.name}` === entityName
                );
                if (foundEntity) {
                    return { ...foundEntity, roles: [], labelsCount: 0 };
                }
            }
        }

        return { ...entity, roles: entity.entityRoleStoreHouse ? entity.entityRoleStoreHouse.items : [] };
    };
    const confusionMatrix: Map<string, EntityConfusion[]> = new Map<string, EntityConfusion[]>();

    if (datasetSwagger.roleConfusions) {
        Object.keys(datasetSwagger.roleConfusions).forEach((id: string) => {
            const roleConfusions = datasetSwagger.roleConfusions[id].map(obj => ({
                typeId: obj.confusionType,
                score: obj.score,
                startTokenIndex: obj.startTokenIndex,
                endTokenIndex: obj.endTokenIndex
            }));
            confusionMatrix.set(id, roleConfusions);
        });
    }
    if (datasetSwagger.entityConfusions) {
        Object.keys(datasetSwagger.entityConfusions).forEach((id: string) => {
            const entityConfusion = datasetSwagger.entityConfusions[id].map(obj => ({
                typeId: obj.confusionType,
                score: obj.score,
                startTokenIndex: obj.startTokenIndex,
                endTokenIndex: obj.endTokenIndex
            }));
            confusionMatrix.set(id, entityConfusion);
        });
    }

    return {
        id: datasetSwagger.id,
        text: datasetSwagger.text,
        // [NOTE THAT] this is not a usable value.
        modifiedDate: new Date(),
        tokenizedText: datasetSwagger.tokenizedText,
        labeledEntities: datasetSwagger.entityLabels
            ? datasetSwagger.entityLabels.map(e => ({
                  entity: entityByName(e.entityName),
                  startTokenIndex: e.startTokenIndex,
                  endTokenIndex: e.endTokenIndex,
                  role: null,
                  startCharIndex: null,
                  endCharIndex: null
              }))
            : null,
        labeledIntent: datasetSwagger.intentLabel ? { ...intentsHouse.findByName(datasetSwagger.intentLabel), features: null } : null,
        predictedEntities: datasetSwagger.entityPredictions
            ? datasetSwagger.entityPredictions.map(e => ({
                  entity: entityByName(e.entityName),
                  startTokenIndex: e.startTokenIndex,
                  endTokenIndex: e.endTokenIndex,
                  role: null,
                  startCharIndex: null,
                  endCharIndex: null
              }))
            : null,
        predictedIntents: datasetSwagger.intentPredictions
            ? datasetSwagger.intentPredictions.map(i => ({
                  intent: { ...intentsHouse.findByName(i.name), features: null },
                  score: i.score
              }))
            : null,
        entityConfusions: confusionMatrix
    };
};

/**
 * Creates a prediction store from published app prediction object
 */
export const trainedPredictionSwaggerToPredictionStore = (endPointSwagger: PredictionSwagger): EndpointUtteranceResult => ({
    text: endPointSwagger.text,
    tokenizedText: endPointSwagger.tokenizedText,
    predictedIntents: endPointSwagger.intentPredictions.map(i => ({
        intent: { id: i.id, name: i.name, typeId: ModelType.Intent, labelsCount: null, features: null },
        score: i.score
    })),
    predictedEntities: endPointSwagger.entityPredictions
        ? endPointSwagger.entityPredictions.map(e => ({
              entity: {
                  id: e.id,
                  name: e.entityName,
                  typeId: e.entityType,
                  roles: null,
                  labelsCount: 0
              },
              startTokenIndex: e.startTokenIndex,
              endTokenIndex: e.endTokenIndex,
              startCharIndex: null,
              endCharIndex: null,
              role: { id: e.roleId, name: e.role }
          }))
        : null,
    predictedPatterns: endPointSwagger.patternPredictions
        ? endPointSwagger.patternPredictions.map(p => ({
              patternName: p.pattern,
              intentName: p.intent,
              order: p.order
          }))
        : null,
    sentiment: endPointSwagger.sentimentAnalysis
});
